import type { DesignActionId, DesignOperationSubmission } from "@afar-research/contracts";

import { withComposerSkillTokens } from "./composerSkillToken.ts";

export type { DesignActionId };

/**
 * Design Operations the Agent Provider carries out. Every member but
 * `delete-design-system` is one: Delete is applied by the host and never
 * becomes a provider turn.
 */
export type DesignAgentActionId = Exclude<DesignActionId, "delete-design-system">;

/**
 * The submissions that reach an Agent Provider. Delete is the one Design
 * Operation with no turn, and since it fans out to host RPCs instead of
 * becoming a queued submission, the prompt formatter below never sees one —
 * this type is how that stays true rather than a case nothing can reach.
 */
export type DesignAgentOperationSubmission = Extract<
  DesignOperationSubmission,
  { readonly action: DesignAgentActionId }
>;

const DESIGN_ACTION_LABELS: Record<DesignActionId, string> = {
  "create-design": "Create Design System",
  "update-design": "Update Design System",
  "add-design-template": "Add Design Template",
  "add-design-example": "Add Design Example",
  "create-design-sample": "Create Design Sample",
  "rename-design-system": "Rename Design System",
  "delete-design-system": "Delete Design System",
};

export function designActionLabel(action: DesignActionId): string {
  return DESIGN_ACTION_LABELS[action];
}

/**
 * The App-owned Skill Bundle a Design Operation runs. The one binding behind
 * both halves of an invocation, exactly as `brainActionSkillBundleId` is for
 * Brain: the chip `formatDesignOperationPrompt` writes into the prompt, and the
 * provider-native dispatch the send resolves against the skill catalog. A
 * prompt that carries the chip without the dispatch leaves the agent to honour
 * a `$name` by reading it, which is not an invocation.
 *
 * Each bundle is named for its action, so this is an identity rather than a
 * table — and stating it here is what keeps the prompt and the dispatch from
 * drifting apart.
 */
export function designActionSkillBundleId(action: DesignAgentActionId): string {
  return action;
}

export function slugifyDesignArtifactName(value: string): string {
  const slug = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return slug.length > 0 ? slug : "untitled";
}

/**
 * How Add Example's `Add ▸ URL` entries ride a brief. Evidence that has no file
 * to attach still has a location, and it folds into the brief rather than
 * widening the wire — so this prefix is the whole contract between the composer
 * that writes the line, the Skill Bundle that reads it, and the bubble that
 * splits it back out into a link chip.
 */
const DESIGN_EVIDENCE_URL_PREFIX = "Evidence URL:";

export function formatDesignEvidenceUrlLine(url: string): string {
  return `${DESIGN_EVIDENCE_URL_PREFIX} ${url}`;
}

/** A brief's own prose and its evidence URLs, told apart by the prefix above. */
export interface DesignBriefParts {
  readonly prose: string;
  readonly evidenceUrls: ReadonlyArray<string>;
}

export function splitDesignBrief(brief: string | undefined): DesignBriefParts {
  const lines = (brief ?? "").split("\n");
  const evidenceUrls: string[] = [];
  const prose: string[] = [];
  for (const line of lines) {
    if (line.startsWith(DESIGN_EVIDENCE_URL_PREFIX)) {
      const url = line.slice(DESIGN_EVIDENCE_URL_PREFIX.length).trim();
      if (url.length > 0) evidenceUrls.push(url);
      continue;
    }
    prose.push(line);
  }
  return { prose: prose.join("\n").trim(), evidenceUrls };
}

function briefSuffix(brief: string | undefined): string {
  const trimmed = brief?.trim() ?? "";
  return trimmed.length > 0 ? ` — ${trimmed}` : "";
}

/** One line naming what the operation does, for a queue entry and a checkpoint. */
export function describeDesignOperation(operation: DesignOperationSubmission): string {
  switch (operation.action) {
    case "create-design":
      return `${operation.displayName}${briefSuffix(operation.brief)}`;
    case "update-design":
      return `Update ${operation.target.name}${briefSuffix(operation.brief)}`;
    // The artifact's name is not in the submission — the Skill Bundle
    // establishes it by grilling — so a queued entry names its target and its
    // brief and nothing else until the turn runs.
    case "add-design-template":
      return `Add a template to ${operation.target.name}${briefSuffix(operation.brief)}`;
    case "add-design-example":
      return `Add an example to ${operation.target.name}${briefSuffix(operation.brief)}`;
    case "create-design-sample":
      return `Create a sample for ${operation.target.name}${briefSuffix(operation.brief)}`;
    case "rename-design-system":
      return `Rename ${operation.target.name}${briefSuffix(operation.brief)}`;
    case "delete-design-system":
      return `Delete ${operation.displayName} (${operation.slug})`;
  }
}

/**
 * The arranged prompt a Design Operation sends. Every agent-backed operation
 * opens with its Skill Bundle token and names its target by the Design System
 * Mention's path, which is the storage identity — the skills resolve a Design
 * System by name or slug and validate the frontmatter themselves.
 *
 * `Leave changes unstaged.` closes each of them: Design Operations never commit
 * and never stage, so `git status` shows the work before anything is recorded.
 */
export function formatDesignOperationPrompt(operation: DesignAgentOperationSubmission): string {
  const target = "target" in operation ? operation.target : null;
  const targetFields =
    target === null ? [] : [`Design system: ${target.name}`, `Design system path: ${target.path}`];
  const brief =
    "brief" in operation && operation.brief !== undefined && operation.brief.trim().length > 0
      ? [`Brief: ${operation.brief}`]
      : [];

  switch (operation.action) {
    case "create-design": {
      const slug = slugifyDesignArtifactName(operation.displayName);
      return withComposerSkillTokens(
        ["design", designActionSkillBundleId("create-design")],
        [
          "Design action: Create Design System",
          `Display name: ${operation.displayName}`,
          `Slug: ${slug}`,
          ...brief,
          `Write .afar-research/design/design-system/${slug}/DESIGN.md as the sole canonical authority with name and slug frontmatter (slug is the storage identity). Leave changes unstaged.`,
        ],
      );
    }
    case "update-design":
      return withComposerSkillTokens(
        ["design", designActionSkillBundleId("update-design")],
        [
          "Design action: Update Design System",
          ...targetFields,
          ...brief,
          "Preserve the storage slug identity in DESIGN.md. Leave changes unstaged.",
        ],
      );
    case "add-design-template":
      return withComposerSkillTokens(
        ["design", designActionSkillBundleId("add-design-template")],
        [
          "Design action: Add Design Template",
          ...targetFields,
          ...brief,
          "Establish the template's name with the user, then write it under the Design System's templates/ with identity provenance (name, slug, designSystemSlug). Leave changes unstaged.",
        ],
      );
    case "add-design-example":
      return withComposerSkillTokens(
        ["design", designActionSkillBundleId("add-design-example")],
        [
          "Design action: Add Design Example",
          ...targetFields,
          ...brief,
          "Establish the example's name with the user, then write it under the Design System's examples/ with identity provenance (name, slug, designSystemSlug). Leave changes unstaged.",
        ],
      );
    case "create-design-sample":
      return withComposerSkillTokens(
        ["design", designActionSkillBundleId("create-design-sample")],
        [
          "Design action: Create Design Sample",
          ...targetFields,
          ...brief,
          "Establish the sample's name with the user, then write it under the Design System's outputs/ as a self-contained Design Sample. Leave changes unstaged.",
        ],
      );
    case "rename-design-system":
      return withComposerSkillTokens(
        ["design", designActionSkillBundleId("rename-design-system")],
        [
          "Design action: Rename Design System",
          ...targetFields,
          ...brief,
          "Establish the new display name with the user before changing anything — the brief above is what they want, not the name itself. Then derive the new storage slug from that name, refuse the rename if that slug is already taken, and do all three jobs: move the Design System's directory, rewrite DESIGN.md's name and slug while preserving id, and rewrite designSystemSlug provenance across every Template, Example, and Sample under it. Report the slug you landed on. Leave changes unstaged.",
        ],
      );
  }
}
